import toLowerCase from 'lodash/toLower'
import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import find from 'lodash/find'
import sumBy from 'lodash/sumBy'
import * as dt from 'app/format/date'
import {toggleLoader} from 'app/redux/loaderSlice'
import {showMessage} from 'app/redux/messageSlice'
import fulfillmentService from 'app/features/orders/services/FulfillmentService'
import {resetDeliveryMethods} from 'app/features/orders/redux/shippingService/deliveryMethodsSlice'
import {RootState} from 'setup'
import {filter} from 'lodash'
import {mapToCreateCarrierFulfillmentInput, mapToCreateFulfillmentInput} from './helper'
import {apiResponseStatus} from 'app/constant'
import {setLabelErrors} from '../shippingLabelError/shippingLabelErrorSlice'

//import history from 'app/history'

type FulfillmentState = {
  isLoading: boolean
  isSubmitting: boolean
  order: any | null
  tracking: {
    trackingNumber: string
    companyName: string
  }
  totalWeight: any
  shippingDates: any[]
  fulfillAt: any
  shippingDefinition: {
    package: any
    labelType: string
    imageType: string
    deliveryMethod: any
    weight: any
  }
}

const initialState: FulfillmentState = {
  isLoading: false,
  isSubmitting: false,
  order: null,
  tracking: {
    trackingNumber: '',
    companyName: '',
  },
  totalWeight: 0,
  shippingDates: getNext7Days(),
  fulfillAt: dt.toYMD(),
  shippingDefinition: {
    package: {},
    labelType: '',
    imageType: '',
    deliveryMethod: {},
    weight: {},
  },
}

export const getOrder = createAsyncThunk('fulfillment/getOrder', async (orderId: string) => {})

export const createFulfillment = createAsyncThunk(
  'fulfillment/save',
  async ({order: fulfillment, history, state}: any, {dispatch, rejectWithValue}) => {
    try {
      dispatch(setSubmitting(true))
      dispatch(toggleLoader(true))
      await fulfillmentService.createFulfillment(mapToCreateFulfillmentInput(fulfillment))
      dispatch(showMessage({text: 'Fulfillment created successfully', variant: 'success'}))
      dispatch(resetDeliveryMethods())
      const {order} = fulfillment
      history({
        pathname: `/orders/${order.id}/fulfillments/view`,
        state,
      })
      return initialState
    } catch (error) {
      console.error(error)
      dispatch(showMessage({text: 'Error occurred while saving', variant: 'error'}))
      return rejectWithValue(error)
    } finally {
      dispatch(toggleLoader(false))
      dispatch(setSubmitting(false))
    }
  }
)

export const createCarrierFulfillment = createAsyncThunk(
  'fulfillment/save',
  async ({order: fulfillment, history}: any, {dispatch, rejectWithValue}) => {
    try {
      dispatch(setSubmitting(true))
      dispatch(toggleLoader(true))
      dispatch(setLabelErrors([]))
      const model = mapToCreateCarrierFulfillmentInput(fulfillment)
      const result: any = await fulfillmentService.createCarrierFulfillment(model)
      if (toLowerCase(result.status) === apiResponseStatus.succeeded) {
        dispatch(showMessage({text: 'Fulfillment created successfully', variant: 'success'}))
        dispatch(resetDeliveryMethods())
        const {order} = fulfillment
        history(`/orders/${order.id}/fulfillments/view`, {replace: true})
        return initialState
      }
      dispatch(setLabelErrors(result.errors))
    } catch (error) {
      console.error(error)
      dispatch(showMessage({text: 'Error occurred while saving', variant: 'error'}))
      return rejectWithValue(error)
    } finally {
      dispatch(toggleLoader(false))
      dispatch(setSubmitting(false))
    }
  }
)

export const clearFulfillmentState = () => (dispatch: any) => {
  dispatch(resetDeliveryMethods())
  dispatch(resetFulfillmentState())
}

const order = createSlice({
  name: 'fulfillment/order',
  initialState,
  reducers: {
    setOrder: (state, action) => {
      const {orderItems, ...rest} = action.payload
      state.order = {
        ...rest,
        orderItems: orderItems.nodes.map((item: any) => ({
          ...item,
          fulfillmentQuantity: item.fulfillableQuantity,
        })),
      }
      state.totalWeight = calculateTotalWeight(state.order.orderItems)
    },

    setTracking: (state, action) => {
      state.tracking = {
        ...state.tracking,
        ...action.payload,
      }
    },

    setPhoneNumber: (state, action) => {
      state.order = {
        ...state.order,
        shippingAddress: {
          ...state.order.shippingAddress,
          phone: action.payload,
        },
      }
    },

    setFulfillmentItemQuantity: (state, action) => {
      const order = {
        ...state.order,
        orderItems: [...state.order.orderItems],
      }
      const {id, quantity} = action.payload
      const orderItem = find(order.orderItems, {id})
      orderItem.fulfillmentQuantity = quantity ? parseInt(quantity) : ''
      state.order = order
      state.totalWeight = calculateTotalWeight(order.orderItems, state.shippingDefinition.package)
    },

    setShippingPackage: (state, action) => {
      const {__typename, ...rest} = action.payload
      state.shippingDefinition.package = rest
      const {order} = state
      const weight = calculateTotalWeight(order.orderItems, action.payload)
      state.totalWeight = weight
      state.shippingDefinition.weight = {
        value: weight,
        units: 'lb',
      }
    },

    setTotalWeight: (state, action) => {
      const weight = action.payload ? parseFloat(action.payload) : 0
      state.totalWeight = action.payload ? parseFloat(action.payload) : 0
      state.shippingDefinition.weight = {
        value: weight,
        units: 'lb',
      }
    },

    setDeliveryMethod: (state, action) => {
      state.shippingDefinition.deliveryMethod = action.payload
    },

    setShippingDate: (state, action) => {
      state.fulfillAt = action.payload
    },

    setShippingLabelImageType: (state, action) => {
      state.shippingDefinition.imageType = action.payload
    },
    setShippingLabelType: (state, action) => {
      state.shippingDefinition.labelType = action.payload
    },

    setSubmitting: (state, action) => {
      state.isSubmitting = action.payload
    },

    resetFulfillmentState: () => {
      return initialState
    },
  },
  extraReducers: {
    [getOrder.fulfilled.toString()]: (state, action) => {},
    [createFulfillment.fulfilled.toString()]: (state, action) => action.payload,
  },
})

export const {
  setOrder,
  setTracking,
  setFulfillmentItemQuantity,
  setShippingPackage,
  setTotalWeight,
  setShippingDate,
  setShippingLabelImageType,
  setShippingLabelType,
  resetFulfillmentState,
  setDeliveryMethod,
  setSubmitting,
  setPhoneNumber,
} = order.actions

export default order.reducer

export const selectFulfillmentOrderItems = ({orders}: RootState): any[] =>
  orders.fulfillment.order.order?.orderItems

export const selectFulfillmentTracking = ({orders}: RootState): any =>
  orders.fulfillment.order.tracking

export const selectFulfillmentViewOrder = ({orders}: RootState): any =>
  orders.fulfillment.order.order

const calculateTotalWeight = (orderItems: any, shippingPackage?: any) => {
  const filterItems = filter(orderItems, ({fulfillmentQuantity}) => fulfillmentQuantity > 0)
  let totalWeight = sumBy(filterItems, (item: any) => {
    const {weight} = item.variant
    return parseFloat(weight.value) * item.fulfillmentQuantity
  })
  if (shippingPackage) {
    const {weight} = shippingPackage
    if (weight) {
      totalWeight += parseFloat(weight.value)
    }
  }
  return parseFloat(totalWeight.toFixed(2))
}

function getNext7Days() {
  const today = dt.toDay()
  const days = [
    {
      date: dt.toYMD(today),
      label: 'Today',
    },
  ]
  for (let i = 0; i < 7; i++) {
    const date = today.add(1, 'days')
    const day = {
      date: dt.toYMD(date),
      label: date.format('ddd, MMM D'),
    }
    days.push(day)
  }
  return days
}
